import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, MapPin, Phone, Mail, Linkedin, Twitter, Instagram, Youtube } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    products: [
      { name: 'Lion Classic', path: '/products/lion-classic' },
      { name: 'Lion Supreme', path: '/products/lion-supreme' },
      { name: 'Hathi Cement', path: '/products/hathi-cement' },
      { name: 'AAC Blocks', path: '/products/efcon-aac-block-sand' },
      { name: 'View All Products', path: '/products' },
    ],
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Media Hub', path: '/media' },
      { name: 'Blog', path: '/blog' },
      { name: 'Our Locations', path: '/locations' },
    ],
    support: [
      { name: 'Contact Us', path: '/contact' },
      { name: 'Smart Tools', path: '/tools' },
      { name: 'Become a Distributor', path: '/distributors' },
      { name: 'Dealer Application', path: '/dealer-form' },
      { name: 'Download Brochure', path: 'https://customer-assets.emergentagent.com/job_44559d10-0942-4033-9eaf-847d47e0afa5/artifacts/8xilewpo_Avi-Cement-Limited-Brochure.pdf', external: true },
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms & Conditions', path: '/terms' },
      { name: 'Legal Disclaimer', path: '/legal-disclaimer' },
      { name: 'Refund Policy', path: '/refund-policy' },
    ],
  };

  return (
    <footer className="bg-surface border-t border-white/5 pb-14 sm:pb-0" data-testid="main-footer">
      {/* CTA Section */}
      <div className="border-b border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16 md:py-24">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                Ready to build<br />
                <span className="text-gradient">something great?</span>
              </h2>
              <p className="text-gray-400 max-w-md">
                Join 500+ dealers and contractors who trust Avi Cement for their projects.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-6 py-3 rounded-full transition-all group"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/distributors"
                className="flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-display font-medium px-6 py-3 rounded-full transition-all"
              >
                Partner With Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <img 
              src="/logo.png" 
              alt="Avi Cement Limited - Lion Cement Manufacturer Gujarat" 
              loading="lazy"
              width="120"
              height="40"
              className="h-10 mb-6"
            />
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Building India's future with quality cement and construction materials since 2004.
            </p>
            <div className="flex gap-4">
              {[Linkedin, Twitter, Instagram, Youtube].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-10 h-10 bg-surface-light hover:bg-coral/20 flex items-center justify-center rounded-full transition-colors group"
                >
                  <Icon className="w-4 h-4 text-gray-500 group-hover:text-coral transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Products</h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-gray-500 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-gray-500 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-white text-sm transition-colors"
                      data-testid="footer-brochure-link"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link 
                      to={link.path}
                      className="text-gray-500 hover:text-white text-sm transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-gray-500 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="tel:+918140044100"
                  className="flex items-center gap-2 text-gray-500 hover:text-white text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  +91 8140 044100
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@avicement.com"
                  className="flex items-center gap-2 text-gray-500 hover:text-white text-sm transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  info@avicement.com
                </a>
              </li>
              <li>
                <span className="flex items-start gap-2 text-gray-500 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Ahmedabad, Gujarat
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-sm">
              © {currentYear} Avi Cement Limited. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="text-gray-600 hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-gray-600 hover:text-white transition-colors">
                Terms
              </Link>
              <Link to="/legal-disclaimer" className="text-gray-600 hover:text-white transition-colors">
                Disclaimer
              </Link>
              <a 
                href="https://avicement.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-white flex items-center gap-1 transition-colors"
              >
                Original Site <ArrowUpRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
